// Build a timed itinerary schedule with optional lunch/dinner/hotel breaks.
import type { LangCode } from "./i18n";
import { LANDING_BAGGAGE_MIN, AIRPORT_TRANSFER_DEFAULT_MIN, HOTEL_CHECKIN_MIN } from "./arrival-window";
import { findWindow, type DayWindow } from "./day-windows";

export type TimedStop = {
  type: "stop";
  idx: number;
  arriveAt: Date;
  leaveAt: Date;
  /** Experiência noturna (vista exterior ou aberto à noite): fica depois do jantar. */
  evening?: boolean;
};
export type TimedBreak = {
  type: "break";
  meal:
    | "lunch"
    | "dinner"
    | "hotel"
    | "breakfast"
    | "arrival"
    | "airport"
    /** Saída: check-out do hotel escolhido. */
    | "checkout"
    /** Saída: deslocação hotel/cidade → aeroporto. */
    | "departure_transfer"
    /** Saída: margem conservadora no aeroporto. */
    | "airport_margin"
    /** Saída: hora do voo. */
    | "flight"
    /** Saída da cidade sem voo declarado. */
    | "departure";
  startAt: Date;
  endAt: Date;
  afterStopIdx: number; // insert after this stop
  day?: number;          // useful for hotel breaks (which day they end)
};
export type TimedEntry = TimedStop | TimedBreak;

/** Blocos logísticos: nunca são refeições nem visitas. */
export const LOGISTICS_MEALS: ReadonlySet<string> = new Set([
  "arrival",
  "airport",
  "checkout",
  "departure_transfer",
  "airport_margin",
  "flight",
  "departure",
]);

/** Blocos da cadeia de saída (nunca podem ser removidos pela cobertura de dias). */
export const DEPARTURE_MEALS: ReadonlySet<string> = new Set([
  "checkout",
  "departure_transfer",
  "airport_margin",
  "flight",
  "departure",
]);

export type LogisticsMeal =
  | "arrival"
  | "airport"
  | "checkout"
  | "departure_transfer"
  | "airport_margin"
  | "flight"
  | "departure";

/** Type guard: permite estreitar o tipo da refeição fora dos blocos logísticos. */
export function isLogisticsMeal(meal: string): meal is LogisticsMeal {
  return LOGISTICS_MEALS.has(meal);
}

export type MealPrefs = {
  lunchStart: string; // "HH:MM"
  lunchEnd: string;
  dinnerStart: string;
  dinnerEnd: string;
  eatMinutes: number;
};

export const DEFAULT_MEAL_PREFS: MealPrefs = {
  lunchStart: "12:00",
  lunchEnd: "14:00",
  dinnerStart: "19:00",
  dinnerEnd: "21:00",
  eatMinutes: 90,
};

export type ScheduleInput = {
  startTime: string; // "HH:MM"
  /** Visit duration at each stop in minutes (defaults to 30 if missing). */
  visitMinutes?: number;
  /** Transit minutes from previous stop (index 0 = 0). Optional `day` (1..N) resets the clock to startTime when it changes. */
  stops: { name: string; transitMinutes?: number; day?: number; evening?: boolean }[];
  /** Meal window overrides. Falls back to DEFAULT_MEAL_PREFS. */
  mealPrefs?: Partial<MealPrefs>;
  /**
   * Preferência vinculativa de refeições. "none" (ou eatMinutes = 0) remove
   * completamente almoço/jantar — o tempo fica disponível para visitas.
   */
  mealPreference?: "restaurant" | "free_break" | "none";
  /**
   * Bloco único de pequeno-almoço/brunch no início do dia 1 ("HH:MM").
   * Nunca se sobrepõe à primeira visita: o relógio das paragens seguintes é
   * recalculado a partir do fim deste bloco.
   */
  breakfast?: { start: string; end: string } | null;
  /**
   * Logística de chegada do dia 1 (aterro/chegada à cidade/hotel). Quando
   * presente, a janela útil do dia 1 só começa depois do overhead
   * (desembarque + bagagem + transferência, e check-in quando o hotel está
   * confirmado). "city" não gera bloco — a rota começa na hora declarada.
   */
  arrival?: {
    kind: "airport" | "city" | "hotel";
    timeMinutes: number;
    includeCheckin?: boolean;
  } | null;
  /**
   * Janelas independentes por dia. Quando presentes, cada dia começa na sua
   * própria hora — a chegada do dia 1 nunca é herdada pelos dias seguintes.
   */
  dayWindows?: DayWindow[];
  /**
   * Saída do último dia: bloco rígido de deslocação ao aeroporto (já com a
   * margem conservadora incluída no `blockStartMinutes`). `includeCheckout`
   * só é verdadeiro quando existe hotel escolhido.
   */
  departure?: {
    blockStartMinutes: number;
    blockEndMinutes?: number;
    includeCheckout?: boolean;
    /** Saída por voo: gera transferência + margem + voo. */
    isAirport?: boolean;
    /** Hora do voo (minutos do dia), quando declarada. */
    departureTimeMinutes?: number;
    /** Duração da deslocação para o aeroporto. */
    transferMinutes?: number;
  } | null;
  /**
   * Paragens com experiência noturna confirmada; a última de cada dia é
   * apresentada DEPOIS do jantar desse dia.
   */
};

/** Minutos de visita que cabem num dia, dado o orçamento e o ritmo. */
export const stopsForMinutes = (minutes: number, visitMinutes: number, transitMinutes: number) =>
  Math.max(1, Math.floor(minutes / (visitMinutes + transitMinutes)));

const HOTEL_DURATION = 30;             // minutes shown for hotel "check-in" block

function hhmmToMin(s: string, fallback: number): number {
  const [h, m] = (s || "").split(":").map((n) => Number.parseInt(n, 10));
  if (!Number.isFinite(h) || !Number.isFinite(m)) return fallback;
  return h * 60 + m;
}

function parseTime(hhmm: string): Date {

  const [h, m] = hhmm.split(":").map((n) => Number.parseInt(n, 10));
  const d = new Date();
  d.setHours(Number.isFinite(h) ? h : 9, Number.isFinite(m) ? m : 0, 0, 0);
  return d;
}

export function buildSchedule(input: ScheduleInput): TimedEntry[] {
  const visit = input.visitMinutes ?? 30;
  const mp = { ...DEFAULT_MEAL_PREFS, ...(input.mealPrefs || {}) };
  const LUNCH_START_MIN = hhmmToMin(mp.lunchStart, 12 * 60);
  const LUNCH_END_MIN = hhmmToMin(mp.lunchEnd, 14 * 60);
  const DINNER_START_MIN = hhmmToMin(mp.dinnerStart, 19 * 60);
  const DINNER_END_MIN = hhmmToMin(mp.dinnerEnd, 21 * 60);
  const mealsOff =
    input.mealPreference === "none" || Number(mp.eatMinutes) <= 0;
  const BREAK_DURATION = Math.max(15, Math.min(240, mp.eatMinutes || 90));
  const entries: TimedEntry[] = [];
  const baseStart = parseTime(input.startTime).getTime();
  const baseMidnight = new Date(baseStart);
  baseMidnight.setHours(0, 0, 0, 0);
  const baseMidnightMs = baseMidnight.getTime();
  const defaultStartMinutes = (baseStart - baseMidnightMs) / 60_000;
  /** Início real do dia N: janela do dia quando existe, senão a hora global. */
  const dayStartMs = (day: number) => {
    const window = input.dayWindows ? findWindow(input.dayWindows, day) : undefined;
    const minutes = window ? window.startMinutes : defaultStartMinutes;
    return baseMidnightMs + (day - 1) * 24 * 60 * 60_000 + minutes * 60_000;
  };
  /**
   * Fim rígido do dia N. Uma paragem normal nunca pode terminar depois desta
   * hora: era assim que apareciam visitas a acabar às 23:51.
   */
  const dayEndMs = (day: number) => {
    const window = input.dayWindows ? findWindow(input.dayWindows, day) : undefined;
    if (!window) return Number.POSITIVE_INFINITY;
    return baseMidnightMs + (day - 1) * 24 * 60 * 60_000 + window.endMinutes * 60_000;
  };

  let currentDay = input.stops[0]?.day ?? 1;
  let cursor = dayStartMs(currentDay);

  // Logística de chegada no dia 1, ANTES do pequeno-almoço: o relógio das
  // visitas só começa depois do desembarque/bagagem/transferência/check-in.
  if (currentDay === 1 && input.arrival && input.arrival.timeMinutes != null && input.stops.length) {
    const dayMidnight = new Date(baseStart);
    dayMidnight.setHours(0, 0, 0, 0);
    cursor = dayMidnight.getTime() + input.arrival.timeMinutes * 60_000;
    const kind = input.arrival.kind;
    const airportMin =
      kind === "airport" ? LANDING_BAGGAGE_MIN + AIRPORT_TRANSFER_DEFAULT_MIN : 0;
    if (airportMin > 0) {
      const startAt = new Date(cursor);
      cursor += airportMin * 60_000;
      entries.push({ type: "break", meal: "airport", startAt, endAt: new Date(cursor), afterStopIdx: -1, day: 1 });
    }
    const checkinMin =
      kind === "hotel" || (kind === "airport" && input.arrival.includeCheckin) ? HOTEL_CHECKIN_MIN : 0;
    if (checkinMin > 0) {
      const startAt = new Date(cursor);
      cursor += checkinMin * 60_000;
      entries.push({ type: "break", meal: "arrival", startAt, endAt: new Date(cursor), afterStopIdx: -1, day: 1 });
    }
    // As janelas de refeição ancoram na hora real do início das visitas.
    cursor = Math.max(cursor, dayMidnight.getTime() + 9 * 60 * 60_000);
  }
  let startMs = cursor;
  let lunchPlaced = false;
  let dinnerPlaced = false;

  // Bloco único de pequeno-almoço/brunch, antes da primeira visita.
  if (input.breakfast && input.stops.length) {
    const winStart = hhmmToMin(input.breakfast.start, 8 * 60 + 30);
    const winEnd = hhmmToMin(input.breakfast.end, winStart + 60);
    const midnight = new Date(cursor);
    midnight.setHours(0, 0, 0, 0);
    const startAtMs = Math.max(cursor, midnight.getTime() + winStart * 60_000);
    const duration = Math.max(30, Math.min(120, winEnd - winStart || 60));
    const startAt = new Date(startAtMs);
    const endAt = new Date(startAtMs + duration * 60_000);
    entries.push({ type: "break", meal: "breakfast", startAt, endAt, afterStopIdx: -1, day: currentDay });
    cursor = endAt.getTime();
    startMs = cursor;
  }


  // Multi-day detection: any stop with day > 1
  const totalDays = input.stops.reduce((max, s) => Math.max(max, s.day ?? 1), 1);
  const isMultiDay = totalDays > 1;

  const placeBreakIfNeeded = (afterIdx: number) => {
    if (mealsOff) return;
    const elapsedMin = (cursor - startMs) / 60000;
    const startD = new Date(startMs);
    const minutesOfDay = startD.getHours() * 60 + startD.getMinutes() + elapsedMin;
    // Partida declarada: nenhuma refeição depois do início da saída.
    const fitsBeforeDeparture =
      departureLimitMs == null ||
      !isLastDay(currentDay) ||
      cursor + BREAK_DURATION * 60_000 <= departureLimitMs;
    if (!fitsBeforeDeparture) return;

    if (!lunchPlaced && minutesOfDay >= LUNCH_START_MIN && minutesOfDay <= LUNCH_END_MIN) {

      const startAt = new Date(cursor);
      cursor += BREAK_DURATION * 60_000;
      const endAt = new Date(cursor);
      entries.push({ type: "break", meal: "lunch", startAt, endAt, afterStopIdx: afterIdx, day: currentDay });
      lunchPlaced = true;
    } else if (!dinnerPlaced && minutesOfDay >= DINNER_START_MIN && minutesOfDay <= DINNER_END_MIN) {

      const startAt = new Date(cursor);
      cursor += BREAK_DURATION * 60_000;
      const endAt = new Date(cursor);
      entries.push({ type: "break", meal: "dinner", startAt, endAt, afterStopIdx: afterIdx, day: currentDay });
      dinnerPlaced = true;
    }
  };

  // Último dia com partida declarada: nada pode ser agendado depois do início
  // do bloco de saída (check-out incluído).
  const departureLimitMs = (() => {
    if (!input.departure) return null;
    const lastDay = input.stops.reduce((max, s) => Math.max(max, s.day ?? 1), 1);
    const lastMidnight = baseMidnightMs + (lastDay - 1) * 24 * 60 * 60_000;
    const blockStart = lastMidnight + input.departure.blockStartMinutes * 60_000;
    return input.departure.includeCheckout
      ? blockStart - HOTEL_CHECKIN_MIN * 60_000
      : blockStart;
  })();
  const isLastDay = (day: number) =>
    day === input.stops.reduce((max, s) => Math.max(max, s.day ?? 1), 1);

  const placeDinnerForClosingDay = (afterIdx: number) => {
    if (mealsOff || dinnerPlaced) return;
    // Anchor to today's DINNER_START_MIN, or right after the last stop if that's later.
    const dayAnchor = new Date(startMs);
    dayAnchor.setHours(0, 0, 0, 0);
    const dinnerStartMs = dayAnchor.getTime() + DINNER_START_MIN * 60_000;
    const startAtMs = Math.max(cursor, dinnerStartMs);
    // Voo/partida declarada: não se janta depois de sair para o aeroporto.
    if (departureLimitMs != null && isLastDay(currentDay) && startAtMs + BREAK_DURATION * 60_000 > departureLimitMs) {
      dinnerPlaced = true;
      return;
    }
    const startAt = new Date(startAtMs);
    const endAt = new Date(startAtMs + BREAK_DURATION * 60_000);
    entries.push({ type: "break", meal: "dinner", startAt, endAt, afterStopIdx: afterIdx, day: currentDay });
    cursor = endAt.getTime();
    dinnerPlaced = true;
  };

  /**
   * Almoço garantido ao fechar o dia.
   *
   * O almoço só era criado ENQUANTO havia paragens a agendar e o relógio caía
   * entre 12:00 e 14:00. Um dia cujas visitas acabam às 11h nunca chegava a
   * essa janela: ficava sem almoço e com o jantar solto às 19h. Agora o almoço
   * tem o mesmo tratamento do jantar — é ancorado na sua janela, desde que
   * caiba na janela do dia e antes da partida declarada.
   */
  const placeLunchForClosingDay = (afterIdx: number) => {
    if (mealsOff || lunchPlaced) return;
    const dayAnchor = new Date(startMs);
    dayAnchor.setHours(0, 0, 0, 0);
    const lunchStartMs = dayAnchor.getTime() + LUNCH_START_MIN * 60_000;
    const latestStartMs = dayAnchor.getTime() + LUNCH_END_MIN * 60_000;
    const startAtMs = Math.max(cursor, lunchStartMs);
    // Já passou a janela de almoço: não se inventa um almoço às 16h.
    if (startAtMs > latestStartMs) return;
    const endAtMs = startAtMs + BREAK_DURATION * 60_000;
    if (departureLimitMs != null && isLastDay(currentDay) && endAtMs > departureLimitMs) return;
    if (endAtMs > dayEndMs(currentDay)) return;
    entries.push({
      type: "break",
      meal: "lunch",
      startAt: new Date(startAtMs),
      endAt: new Date(endAtMs),
      afterStopIdx: afterIdx,
      day: currentDay,
    });
    cursor = Math.max(cursor, endAtMs);
    lunchPlaced = true;
  };



  input.stops.forEach((s, i) => {
    const day = s.day ?? currentDay;
    if (day !== currentDay) {
      const prevIdx = i - 1;
      // Multi-day sightseeing usually ends before 19:00, so the in-loop dinner
      // window never fires. Force lunch and dinner for the day we are closing.
      if (prevIdx >= 0) placeLunchForClosingDay(prevIdx);
      if (isMultiDay && prevIdx >= 0) placeDinnerForClosingDay(prevIdx);
      // Day changed: place hotel break BEFORE resetting to the next day.
      if (isMultiDay && i > 0) {
        const startAt = new Date(cursor + 30 * 60_000);
        const endAt = new Date(startAt.getTime() + HOTEL_DURATION * 60_000);
        entries.push({ type: "break", meal: "hotel", startAt, endAt, afterStopIdx: prevIdx, day: currentDay });
      }
      currentDay = day;
      cursor = dayStartMs(currentDay);
      startMs = cursor;
      lunchPlaced = false;
      dinnerPlaced = false;
    }
    // Experiência noturna na última paragem do dia: o jantar do dia é
    // ancorado ANTES dela, para a visita ficar depois de jantar.
    const isLastOfCurrentDay = (input.stops[i + 1]?.day ?? day) !== day;
    if (s.evening && isLastOfCurrentDay && !dinnerPlaced) {
      placeDinnerForClosingDay(i - 1);
    }
    const isFirstOfDay = i === 0 || (input.stops[i - 1]?.day ?? currentDay) !== day;
    const transit = isFirstOfDay ? 0 : s.transitMinutes ?? 10;
    const arriveMs = cursor + transit * 60_000;
    const leaveMs = arriveMs + visit * 60_000;
    // Barreira rígida da janela do dia: fora dela a paragem não é agendada.
    if (leaveMs > dayEndMs(day)) return;
    cursor = leaveMs;
    entries.push({
      type: "stop",
      idx: i,
      arriveAt: new Date(arriveMs),
      leaveAt: new Date(leaveMs),
      ...(s.evening ? { evening: true } : {}),
    });
    placeBreakIfNeeded(i);
  });

  // Ensure the final day also gets lunch and dinner entries.
  if (input.stops.length > 0) {
    placeLunchForClosingDay(input.stops.length - 1);
  }
  if (isMultiDay && input.stops.length > 0) {
    placeDinnerForClosingDay(input.stops.length - 1);
  }

  // Saída do último dia: check-out (só com hotel escolhido) e deslocação para
  // o aeroporto. Blocos rígidos — a barreira final garante que nada fica
  // depois deles.
  if (input.departure && input.stops.length) {
    const lastDay = totalDays;
    const lastIdx = input.stops.length - 1;
    const lastMidnight = baseMidnightMs + (lastDay - 1) * 24 * 60 * 60_000;
    const blockStart = lastMidnight + input.departure.blockStartMinutes * 60_000;
    const push = (meal: TimedBreak["meal"], startMs: number, endMs: number) => {
      entries.push({
        type: "break",
        meal,
        startAt: new Date(startMs),
        endAt: new Date(Math.max(endMs, startMs + 5 * 60_000)),
        afterStopIdx: lastIdx,
        day: lastDay,
      });
    };
    // Check-out só existe com hotel realmente escolhido.
    if (input.departure.includeCheckout) {
      push("checkout", blockStart - HOTEL_CHECKIN_MIN * 60_000, blockStart);
    }
    if (input.departure.isAirport) {
      const transfer = Math.max(15, input.departure.transferMinutes ?? AIRPORT_TRANSFER_DEFAULT_MIN);
      const transferEnd = blockStart + transfer * 60_000;
      push("departure_transfer", blockStart, transferEnd);
      const flightMs =
        input.departure.departureTimeMinutes != null
          ? lastMidnight + input.departure.departureTimeMinutes * 60_000
          : transferEnd + 150 * 60_000;
      // Margem de aeroporto explícita: termina exatamente à hora do voo.
      push("airport_margin", transferEnd, Math.max(flightMs, transferEnd + 15 * 60_000));
      push("flight", Math.max(flightMs, transferEnd + 15 * 60_000), Math.max(flightMs, transferEnd + 15 * 60_000) + 15 * 60_000);
    } else {
      const blockEnd =
        input.departure.blockEndMinutes != null
          ? lastMidnight + input.departure.blockEndMinutes * 60_000
          : blockStart + 30 * 60_000;
      push("departure", blockStart, blockEnd);
    }
  }

  return sortTimedEntries(entries);
}

/**
 * Ordena qualquer lista de entradas temporais por hora de início (estável).
 * Depois de qualquer inserção — refeição, hotel, check-out, aeroporto — a
 * agenda tem de ficar cronológica antes de ser validada ou apresentada.
 */
export function sortTimedEntries(entries: TimedEntry[]): TimedEntry[] {
  const at = (e: TimedEntry) => (e.type === "stop" ? e.arriveAt : e.startAt).getTime();
  return entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => at(a.entry) - at(b.entry) || a.index - b.index)
    .map((x) => x.entry);
}

/**
 * Invariantes cronológicos da agenda: horas sempre crescentes e fim depois do
 * início. Puro; usado antes de renderizar, exportar ou guardar.
 */
export function isChronological(entries: TimedEntry[]): boolean {
  const start = (e: TimedEntry) => (e.type === "stop" ? e.arriveAt : e.startAt).getTime();
  const end = (e: TimedEntry) => (e.type === "stop" ? e.leaveAt : e.endAt).getTime();
  for (let i = 0; i < entries.length; i++) {
    if (end(entries[i]) <= start(entries[i])) return false;
    if (i > 0 && start(entries[i]) < start(entries[i - 1])) return false;
  }
  return true;
}


export function formatTime(d: Date, lang: LangCode): string {
  const use24h = lang !== "en";
  return d.toLocaleTimeString(lang, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: !use24h,
  });
}

/**
 * Corta as paragens do ÚLTIMO dia que começam depois do limite de partida
 * ("o meu voo é às 20h" → visitas até à margem de aeroporto). Pausas depois
 * da última paragem mantida (jantar ancorado) também são removidas. Puro e
 * determinístico; sem partida declarada não faz nada.
 */
export function clipStopsForDeparture(entries: TimedEntry[], cutoffMinutes: number): TimedEntry[] {
  if (!entries.length || cutoffMinutes >= 24 * 60) return entries;
  const midnightOf = (d: Date) => {
    const m = new Date(d);
    m.setHours(0, 0, 0, 0);
    return m.getTime();
  };
  const entryDate = (e: TimedEntry) => (e.type === "stop" ? e.arriveAt : e.startAt);
  const midnights = entries.map((e) => midnightOf(entryDate(e)));
  const base = Math.min(...midnights);
  const dayOf = (i: number) => Math.floor((midnights[i] - base) / 86_400_000) + 1;
  const lastDay = Math.max(...midnights.map((_, i) => dayOf(i)));
  const minutesOfDay = (d: Date) => d.getHours() * 60 + d.getMinutes();
  const out: TimedEntry[] = [];
  let clipping = false;
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    if (dayOf(i) !== lastDay) {
      if (!clipping) out.push(entry);
      continue;
    }
    if (clipping) continue;
    if (entry.type === "stop" && minutesOfDay(entry.arriveAt) >= cutoffMinutes) {
      clipping = true;
      continue;
    }
    // Refeições e dormida nunca podem ficar depois da saída declarada; os
    // blocos logísticos (check-out, transferência, margem, voo) mantêm-se.
    if (
      entry.type === "break" &&
      (entry.meal === "lunch" || entry.meal === "dinner" || entry.meal === "breakfast" || entry.meal === "hotel") &&
      minutesOfDay(entry.startAt) >= cutoffMinutes
    ) {
      continue;
    }
    out.push(entry);

  }
  return out;
}
